import { useModoConsultaEvaluacion } from '../../../../hooks/useModoConsultaEvaluacion';
import React, { useContext, useState } from 'react';
import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { addDoc, collection } from 'firebase/firestore';
import Swal from 'sweetalert2';

import { AppContext } from '../../../../Context';
import { db } from '../../../../firebase/firebase';

const EJES = [
    { id: 'caracterizacion', etiqueta: 'Caracterización del villismo' },
    { id: 'composicionSocial', etiqueta: 'Composición social' },
    { id: 'accionesReformas', etiqueta: 'Acciones y reformas' },
    { id: 'resultadosLimitaciones', etiqueta: 'Resultados y limitaciones' }
];

const textoBase = `EL REINO VILLISTA

De todos los movimientos revolucionarios, el villismo es el más polémico para caracterizar. Las razones son muchas: las leyendas en torno a Francisco Villa y sus seguidores, los cientos de miles que conforman la División del Norte y sus más allegados, los famosos “dorados”; el hecho de que la historia la hacen los vencedores y, una vez derrotada esta facción, se intentó minimizar su imagen tratándola como un conglomerado de bandidos y bandoleros sin programa alguno; y la complejidad de su composición social y sus metas. Hasta analistas serios tienen visiones totalmente contradictorias de este movimiento popular.

Como lo ha mostrado Federico Katz, los campesinos que constituían una parte esencial de este ejército eran un grupo poco integrado, de múltiples ocupaciones, procedencias y clases sociales: colonos a los que se les prometió el regreso de las tierras, pastores, mineros, trabajadores semiindustriales, semiagrícolas, desempleados de fábricas, plantaciones y minas, así como migrantes que iban a probar fortuna a Estados Unidos. Si los zapatistas luchaban por la tierra, los villistas lo hacían para sobrevivir, por las buenas pagas, las altas probabilidades de victoria y el simbolismo que desde el inicio rodeó a su máximo dirigente.

Una de las mejores maneras de conocer el verdadero significado de cualquier facción revolucionaria consiste en ir más allá de las leyes y proclamas formales para adentrarse en las acciones concretas de justicia, política, reforma agraria y del trato otorgado a las clases desfavorecidas dentro de sus regiones de influencia.

Un escaparate ideal de este abigarrado movimiento es la gubernatura que encabezó Villa en Chihuahua. En diciembre de 1913 publicó un decreto clave para su ejército, su administración y la gente que vivía en sus territorios: la confiscación de tierras y otras propiedades que pertenecían a los hacendados mexicanos más ricos y poderosos de Chihuahua. A mediano plazo, lo obtenido se destinaría a la tesorería pública, en especial a las viudas y huérfanos de sus “dorados”. A la larga, después de la victoria, se aprobarían leyes para cambiar radicalmente la estructura de la propiedad en beneficio de los revolucionarios y sus familias, de los antiguos dueños de terrenos usurpados y de campesinos pobres.

En mayo de 1915 se expidió una ley agraria villista con la intención de fraccionar los enormes latifundios en pequeñas propiedades. Cada estado fijaría los límites máximos que un individuo podría poseer y los excedentes se expropiarían, se fraccionarían y se pondrían a la venta. Sin embargo, en contraste con la reforma agraria expedita en tierras morelenses, en las zonas villistas los planes de reestructuración de la propiedad se pondrían en efecto después de la victoria, que nunca llegó.`;

const crearColumnasIniciales = () => Object.fromEntries(
    EJES.map(({ id }) => [id, { ideaPrincipal: '', detalles: '' }])
);

const M14_ColumnasCuadroSinoptico = ({ cerrarModal }) => {
    const { currentUser } = useContext(AppContext);
    const modoConsulta = useModoConsultaEvaluacion();
    const [step, setStep] = useState(1);
    const [enviando, setEnviando] = useState(false);
    const [respuestas, setRespuestas] = useState({
        temaPrincipal: '',
        ejesIdentificados: '',
        columnas: crearColumnasIniciales()
    });

    const actualizarCampo = (campo, valor) => {
        setRespuestas(prev => ({ ...prev, [campo]: valor }));
    };

    const actualizarColumna = (id, campo, valor) => {
        setRespuestas(prev => ({
            ...prev,
            columnas: {
                ...prev.columnas,
                [id]: { ...prev.columnas[id], [campo]: valor }
            }
        }));
    };

    const avanzarAColumnas = () => {
        if (!modoConsulta && (!respuestas.temaPrincipal.trim() || !respuestas.ejesIdentificados.trim())) {
            Swal.fire({
                icon: 'warning',
                title: 'Información incompleta',
                text: 'Escribe el tema principal y los ejes que identificaste antes de continuar.'
            });
            return;
        }
        setStep(3);
    };

    const enviarResultados = async () => {
        if (modoConsulta) { cerrarModal?.(); return; }

        const incompleta = EJES.some(({ id }) => (
            !respuestas.columnas[id].ideaPrincipal.trim()
            || !respuestas.columnas[id].detalles.trim()
        ));

        if (incompleta) {
            Swal.fire({
                icon: 'warning',
                title: 'Cuadro incompleto',
                text: 'Completa la idea principal y los detalles de las cuatro columnas.'
            });
            return;
        }

        setEnviando(true);
        try {
            await addDoc(collection(db, 'evaluaciones'), {
                userId: currentUser.uid,
                nombre: 'Columnas para cuadros sinópticos - El reino villista',
                nivel: 'Avanzado',
                modulo: '14',
                createdAt: new Date(),
                tipo: 'Cuadro sinóptico por columnas',
                respuestas
            });

            await Swal.fire({
                title: '¡Ejercicio completado!',
                text: 'El cuadro sinóptico se guardó correctamente.',
                icon: 'success',
                confirmButtonColor: '#198754',
                confirmButtonText: 'Continuar'
            });
            if (cerrarModal) cerrarModal();
        } catch (error) {
            console.error('Error al guardar el ejercicio:', error);
            Swal.fire({
                icon: 'error',
                title: 'No se pudo guardar',
                text: 'Intenta enviar nuevamente el ejercicio.'
            });
        } finally {
            setEnviando(false);
        }
    };

    return (
        <Container>
            <h3 className="text-center mb-4">Columnas para cuadros sinópticos</h3>

            {step === 1 && (
                <div>
                    <Card className="mb-4 shadow-sm">
                        <Card.Body>
                            <Card.Title>1. Lectura: El reino villista</Card.Title>
                            <Card.Text>
                                Lee el texto con la técnica de tu preferencia. Después organizarás la información
                                en columnas de acuerdo con su función dentro del tema.
                            </Card.Text>
                            <div className="p-3 bg-light rounded" style={{ whiteSpace: 'pre-wrap', textAlign: 'justify' }}>
                                {textoBase}
                            </div>
                        </Card.Body>
                    </Card>
                    <div className="d-flex justify-content-end">
                        <Button onClick={() => setStep(2)}>Siguiente: identificar</Button>
                    </div>
                </div>
            )}

            {step === 2 && (
                <div>
                    <Card className="mb-4 shadow-sm border-warning">
                        <Card.Body>
                            <Card.Title>2. Identifica la estructura</Card.Title>
                            <Card.Text>
                                Resume el asunto central y reconoce los grupos de información que formarán las columnas.
                            </Card.Text>
                        </Card.Body>
                    </Card>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Tema principal</Form.Label>
                            <Form.Control
                                name="temaPrincipal"
                                value={respuestas.temaPrincipal}
                                onChange={event => actualizarCampo('temaPrincipal', event.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Ejes o grupos de información identificados</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                name="ejesIdentificados"
                                value={respuestas.ejesIdentificados}
                                onChange={event => actualizarCampo('ejesIdentificados', event.target.value)}
                            />
                        </Form.Group>
                    </Form>
                    <div className="d-flex justify-content-between">
                        <Button variant="secondary" onClick={() => setStep(1)}>Atrás</Button>
                        <Button onClick={avanzarAColumnas}>Siguiente: construir</Button>
                    </div>
                </div>
            )}

            {step === 3 && (
                <div>
                    <Card className="mb-4 shadow-sm border-success">
                        <Card.Body>
                            <Card.Title>3. Construye el cuadro por columnas</Card.Title>
                            <Card.Text>
                                En cada columna escribe una idea principal y los datos del texto que la explican.
                            </Card.Text>
                        </Card.Body>
                    </Card>
                    <Form>
                        <Row className="g-3">
                            {EJES.map(({ id, etiqueta }) => (
                                <Col xs={12} lg={6} key={id}>
                                    <Card className="h-100 border-dark">
                                        <Card.Header className="bg-dark text-white fw-bold">{etiqueta}</Card.Header>
                                        <Card.Body>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Idea principal</Form.Label>
                                                <Form.Control
                                                    name={`${id}IdeaPrincipal`}
                                                    value={respuestas.columnas[id].ideaPrincipal}
                                                    onChange={event => actualizarColumna(id, 'ideaPrincipal', event.target.value)}
                                                />
                                            </Form.Group>
                                            <Form.Group>
                                                <Form.Label>Detalles que la sustentan</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={4}
                                                    name={`${id}Detalles`}
                                                    value={respuestas.columnas[id].detalles}
                                                    onChange={event => actualizarColumna(id, 'detalles', event.target.value)}
                                                />
                                            </Form.Group>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Form>
                    <div className="d-flex justify-content-between mt-4">
                        <Button variant="secondary" onClick={() => setStep(2)}>Atrás</Button>
                        <Button variant="success" disabled={enviando} onClick={enviarResultados}>
                            {enviando ? 'Guardando…' : 'Finalizar y enviar'}
                        </Button>
                    </div>
                </div>
            )}
        </Container>
    );
};

export default M14_ColumnasCuadroSinoptico;
